import { makeStyles, OptionOnSelectData, SelectionEvents, tokens } from '@fluentui/react-components';
import { Info12Regular } from '@fluentui/react-icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormView } from '../../../contexts/useFormView';
import { IWhitePaperColumn, TaxReturnTOCItem } from '../../../model';
import { buildSectionNavigationList, resolveNavigationIndex } from '../../../utils/formNavigation';
import { findTocItemById, getTocItemFromKeyMap, isIsolatedTocItem } from '../../../utils/TaxReturnTOCUtils';
import CalloutIcon from '../../common/CalloutIcon';
import DefaultSeparator from '../../common/DefaultSeparator';
import { FormNavigation } from '../../form/FormNavigation';
import { FormPageNavigation } from '../../form/FormPageNavigation';
import { FormZoomControls } from '../../form/FormZoomControls';
import EntitySelectorCombobox from './EntitySelectorCombobox';
import { IFormInteractionHandler } from './IFormInteractionHandler';

const useStyles = makeStyles({
    pdfToolbar: {
        width: '100%',
        height: '56px',
        padding: '12px 16px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
        marginBottom: '1px',
        gap: '0.8rem',
        '& > label': {
            fontWeight: tokens.fontWeightSemibold,
        },
    },
    row: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '5px',
    },
});

export interface IFormViewerToolbarProps {
    reportColumns: IWhitePaperColumn[];
    formInteractionHandler: IFormInteractionHandler;
}

const FormViewerToolbar: React.FC<IFormViewerToolbarProps> = ({ reportColumns, formInteractionHandler }) => {
    const styles = useStyles();
    const { t } = useTranslation();
    const { tocData, currentTOCItemState, selectedEntity, loadTocAndFormForEntity, tocReferenceMap, navigateToSectionForm } = useFormView();

    const selectedEntityId = selectedEntity?.id || currentTOCItemState?.entity?.id || reportColumns?.[0]?.columnId;
    const defaultColumn = reportColumns?.find((column) => column.columnId === selectedEntityId);
    const defaultComboboxSelectedOptions = defaultColumn?.columnId ? [defaultColumn.columnId.toString()] : [];
    const defaultComboboxValue = defaultColumn?.description ?? '';

    const selectEntityForForm = (_: SelectionEvents, data: OptionOnSelectData) => {
        const value: string | undefined = data.optionValue;
        if (value) {
            const selectedEntity: IWhitePaperColumn | undefined = reportColumns?.find(
                (column: IWhitePaperColumn): boolean => column.columnId === parseInt(value, 10)
            );

            loadTocAndFormForEntity(selectedEntity);
        }
    };

    const navigationItemsSorted = buildSectionNavigationList(tocData, currentTOCItemState);
    const currentNavigationArrayIndex = resolveNavigationIndex(navigationItemsSorted, currentTOCItemState, tocData, tocReferenceMap);
    const isFirstForm: boolean = currentNavigationArrayIndex === 0;
    const isLastForm: boolean = currentNavigationArrayIndex === navigationItemsSorted.length - 1;

    const currentItemFromKey: TaxReturnTOCItem | undefined = currentTOCItemState?.tocKey
        ? getTocItemFromKeyMap(tocReferenceMap, currentTOCItemState.tocKey)
        : undefined;
    const currentItemFromId: TaxReturnTOCItem | undefined = currentItemFromKey
        ? undefined
        : findTocItemById(tocData?.items, currentTOCItemState?.id ?? undefined);
    const currentItem: TaxReturnTOCItem | undefined = currentItemFromKey ?? currentItemFromId;
    const isCurrentItemIsolated: boolean = currentItem ? isIsolatedTocItem(currentItem, tocData?.items) : false;

    const previousForm = (goToLastPage?: boolean) => {
        const previousIndex = currentNavigationArrayIndex - 1;
        if (previousIndex >= 0) {
            navigateToSectionForm(previousIndex, { goToLastPage });
        }
    };

    const nextForm = () => {
        const nextIndex = currentNavigationArrayIndex + 1;
        if (nextIndex < navigationItemsSorted.length) {
            navigateToSectionForm(nextIndex);
        }
    };

    return (
        <div className={styles.pdfToolbar}>
            <div className={styles.row}>
                {reportColumns?.length > 1 && (
                    <>
                        <label>{t('entity')}</label>
                        <div className={styles.row}>
                            <EntitySelectorCombobox
                                reportColumns={reportColumns}
                                onOptionSelect={selectEntityForForm}
                                selectedOptions={defaultComboboxSelectedOptions}
                                value={defaultComboboxValue}
                                disabled={!currentTOCItemState?.canSelectEntity}
                            />
                            {!currentTOCItemState?.canSelectEntity && (
                                <CalloutIcon
                                    icon={<Info12Regular />}
                                    buttonAriaLabel={t('selectedEntityNotApplicable').toString()}
                                    darkSurface
                                >
                                    {t('selectedEntityNotApplicable').toString()}
                                </CalloutIcon>
                            )}
                        </div>
                    </>
                )}
            </div>
            <div className={styles.row}>
                <FormPageNavigation
                    isFirstForm={isFirstForm}
                    isLastForm={isLastForm}
                    onNavigateToPreviousForm={previousForm}
                    onNavigateToNextForm={nextForm}
                    isIsolatedTocItem={isCurrentItemIsolated}
                />
                <DefaultSeparator height='1.25rem' />
                <FormZoomControls formInteractionHandler={formInteractionHandler} />
                <DefaultSeparator height='1.25rem' />
                <FormNavigation
                    isFirstForm={isFirstForm}
                    isLastForm={isLastForm}
                    onPreviousFormClick={previousForm}
                    onNextFormClick={nextForm}
                    currentItem={currentItem}
                />
            </div>
        </div>
    );
};

export default FormViewerToolbar;
